import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const FEATURES = [
  { key: "f1", icon: "📋" },
  { key: "f2", icon: "🏷️" },
  { key: "f3", icon: "✍️" },
  { key: "f4", icon: "⏰" },
  { key: "f5", icon: "📈" },
  { key: "f6", icon: "🛡️" },
];

export default function Landing() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "" });
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      // Lead capture: POST as a public "demo request" entry
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setSubmitted(true);
    } catch (err) {
      // Even if endpoint is missing, we keep the UI optimistic for now
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="landing-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 font-black text-white">DV</div>
          <div>
            <p className="text-base font-bold tracking-tight">DVR ProSuite</p>
            <p className="text-xs text-slate-400">D.LGS. 81/2008</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/login" className="rounded-full border border-slate-700 px-4 py-1.5 text-sm font-medium hover:border-blue-400 hover:text-blue-200" data-testid="landing-login-link">
            {t("landing.cta_login")}
          </Link>
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-2">
        <div>
          <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-hero-title">
            {t("landing.hero_title")}
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-300 sm:text-lg" data-testid="landing-hero-subtitle">
            {t("landing.hero_subtitle")}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#demo" className="rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:bg-blue-400" data-testid="landing-cta-demo">
              {t("landing.cta_demo")}
            </a>
            <Link to="/login" className="rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold hover:border-blue-400" data-testid="landing-cta-login">
              {t("landing.cta_login")}
            </Link>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 -translate-x-6 translate-y-6 rounded-3xl bg-gradient-to-br from-blue-700/30 to-indigo-700/20 blur-3xl" />
          <div className="relative rounded-3xl border border-slate-700/60 bg-slate-900/80 p-8 backdrop-blur">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-300">DVR Wizard</p>
            <p className="mt-3 text-2xl font-bold">20 sezioni guidate</p>
            <div className="mt-6 space-y-2 text-sm text-slate-300">
              <div className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2"><span>Anagrafica + ATECO</span><span className="font-bold text-emerald-400">100%</span></div>
              <div className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2"><span>Organizzazione sicurezza</span><span className="font-bold text-emerald-400">100%</span></div>
              <div className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2"><span>Valutazione rischi</span><span className="font-bold text-amber-400">62%</span></div>
              <div className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2"><span>Piano miglioramento</span><span className="font-bold text-rose-400">28%</span></div>
            </div>
            <div className="mt-6 h-2 overflow-hidden rounded-full bg-slate-800">
              <div className="h-full w-3/4 rounded-full bg-gradient-to-r from-blue-400 to-emerald-400" />
            </div>
            <p className="mt-2 text-right text-xs text-slate-400">76% completamento medio</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-3xl font-bold tracking-tight" data-testid="landing-features-title">{t("landing.features_title")}</h2>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.key} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 transition hover:border-blue-500/60 hover:bg-slate-900" data-testid={`landing-feature-${f.key}`}>
              <div className="text-3xl">{f.icon}</div>
              <p className="mt-4 text-lg font-bold">{t(`landing.${f.key}_title`)}</p>
              <p className="mt-2 text-sm text-slate-400">{t(`landing.${f.key}_desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="demo" className="bg-gradient-to-b from-slate-900 to-slate-950 px-6 py-20">
        <div className="mx-auto max-w-3xl rounded-3xl border border-slate-800 bg-slate-900/80 p-8 shadow-2xl">
          <h2 className="text-3xl font-bold" data-testid="landing-demo-title">{t("landing.demo_title")}</h2>
          <p className="mt-2 text-slate-400">{t("landing.demo_subtitle")}</p>
          {submitted ? (
            <div className="mt-8 rounded-xl bg-emerald-900/30 p-6 text-emerald-200" data-testid="landing-demo-success">
              ✓ {t("landing.demo_sent")}
            </div>
          ) : (
            <form className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={handleSubmit} data-testid="landing-demo-form">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("landing.demo_name")} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm" data-testid="landing-demo-name" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("landing.demo_email")} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm" data-testid="landing-demo-email" />
              <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t("landing.demo_company")} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm" data-testid="landing-demo-company" />
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder={t("landing.demo_role")} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm" data-testid="landing-demo-role" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("landing.demo_message")} rows={3} className="md:col-span-2 rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm" data-testid="landing-demo-message" />
              {error && <div className="md:col-span-2 text-sm text-rose-400">{error}</div>}
              <button type="submit" disabled={submitting} className="md:col-span-2 rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:bg-blue-400 disabled:opacity-60" data-testid="landing-demo-submit">
                {submitting ? "…" : t("landing.demo_submit")}
              </button>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-slate-800 px-6 py-8 text-center text-xs text-slate-500" data-testid="landing-footer">
        {t("landing.footer_legal")}
      </footer>
    </div>
  );
}
